import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ClassGroupDetail, ClassGroupListRow } from '@repo/shared-types';
import type {
  CreateClassInput, UpdateClassInput, AddClassMembersInput, UpdateClassMemberSlotsInput,
} from '@repo/validation';
import { fetchApi } from '../lib/api';
import i18n from '../i18n';
import { invalidateGettingStarted } from './useGettingStarted';

// Fix wave (review I12) — every toast below used to be a literal Vietnamese
// string. `useClasses.ts` had no "legacy" excuse `useSlots.ts` carries (it's
// a brand-new file on this branch), and it was the one i18n regression on
// the branch — every OTHER toast under `pages/classes/**` goes through
// `t()`. `classes.toast.*` (vi + en, `scheduling.json`).
const ct = (key: string) => i18n.t(`classes.toast.${key}`, { ns: 'scheduling' });

export interface ClassFilters {
  teacherId?: string;
  subjectId?: string;
  branchId?: string;
  /** Mirrors `GET /api/classes`'s own bucket: ACTIVE (default — RUNNING +
   *  UPCOMING) / ENDED (phase ENDED, still an ACTIVE group) / ARCHIVED (the
   *  group itself is archived). */
  status?: 'ACTIVE' | 'ENDED' | 'ARCHIVED';
}

export function useClasses(filters?: ClassFilters, options?: { enabled?: boolean }) {
  const teacherId = filters?.teacherId;
  const subjectId = filters?.subjectId;
  const branchId = filters?.branchId;
  const status = filters?.status;
  const params = new URLSearchParams();
  if (teacherId) params.set('teacherId', teacherId);
  if (subjectId) params.set('subjectId', subjectId);
  if (branchId) params.set('branchId', branchId);
  if (status) params.set('status', status);
  const qs = params.toString();
  return useQuery<ClassGroupListRow[]>({
    queryKey: [
      'classes',
      { teacherId: teacherId ?? null, subjectId: subjectId ?? null, branchId: branchId ?? null, status: status ?? null },
    ],
    queryFn: () => fetchApi(qs ? `/classes?${qs}` : '/classes'),
    enabled: options?.enabled ?? true,
  });
}

export function useClass(id: string | null) {
  return useQuery<ClassGroupDetail>({
    queryKey: ['classes', id],
    queryFn: () => fetchApi(`/classes/${id}`),
    enabled: id !== null,
  });
}

// Every mutation below touches the class layer and everything derived from
// it — the slots it owns, the sessions those slots generate, the enrollments
// a membership edit creates/joins, and the students list's Lớp column/filter
// (Task 10 brief's own invalidation list).
const inval = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['classes'] });
  qc.invalidateQueries({ queryKey: ['slots'] });
  qc.invalidateQueries({ queryKey: ['sessions'] });
  qc.invalidateQueries({ queryKey: ['enrollments'] });
  qc.invalidateQueries({ queryKey: ['students'] });
  // Fix wave (review M8) — creating a class and adding members is now the
  // primary path for checklist steps 2-4 (a slot exists / a student is
  // enrolled / placed in one), same as `useSlots.ts`'s own `inval`.
  invalidateGettingStarted(qc);
};

/** THI-267 Task 11: NO hook-level `onError` toast, unlike this file's other
 *  mutations — `ClassFormSheet` needs bespoke inline handling for
 *  `SLOT_TIME_CONFLICT` / `START_DATE_IN_RECONCILED_MONTH` / immutability
 *  errors (tinted cells, a message under the offending row/field, Lưu
 *  disabled until something changes) and falls back to `toast.error` itself
 *  for anything else, exactly the way `ApplyTemplateDialog` already owns
 *  `useApplyTemplate`'s error handling in full (`useScheduleTemplates.ts`)
 *  rather than racing a hook-level toast against its own `OPEN_PERIOD` UI. */
export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateClassInput) => fetchApi<ClassGroupDetail>('/classes', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success(ct('created')); },
  });
}

export function useUpdateClass(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateClassInput) => fetchApi<ClassGroupDetail>(`/classes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success(ct('updated')); },
  });
}

export function useArchiveClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<{ archived: boolean }>(`/classes/${id}/archive`, { method: 'POST' }),
    onSuccess: () => { inval(qc); toast.success(ct('archived')); },
    onError: (e) => toast.error(e.message || ct('archiveError')),
  });
}

export function useUnarchiveClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<{ unarchived: boolean }>(`/classes/${id}/unarchive`, { method: 'POST' }),
    onSuccess: () => { inval(qc); toast.success(ct('unarchived')); },
    onError: (e) => toast.error(e.message || ct('unarchiveError')),
  });
}

export interface AddClassMemberRowResult {
  index: number;
  outcome: 'created' | 'linked' | 'repriced' | 'skipped';
  studentId: string;
  enrollmentId: string;
}

/** Mirrors `AddClassMembersResult` (`apps/api/src/services/classMemberService.ts`). */
export interface AddClassMembersResult {
  rows: AddClassMemberRowResult[];
  counts: { created: number; linked: number; repriced: number; skipped: number };
}

/** THI-267 Task 13: NO hook-level toast either direction — like
 *  `useCreateClass`/`useUpdateClass` above, `AddMembersDialog`'s confirm
 *  table needs bespoke handling neither a generic toast could give it: the
 *  success toast names the added count and the class's own displayName
 *  ("Đã thêm N học viên vào <lớp>"), and a row-specific 400
 *  (`DUPLICATE_ROW` / `PRICING_TYPE_MISMATCH` / `ENROLLMENT_PAUSED` /
 *  `SLOT_NOT_IN_CLASS` / `RATE_REQUIRED`, every one carrying `rowIndex` —
 *  see lib/api.ts) jumps back to the table and marks that row instead of
 *  toasting. `START_DATE_IN_RECONCILED_MONTH` and the 409 retry message get
 *  their own inline handling there too. */
export function useAddClassMembers(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AddClassMembersInput) =>
      fetchApi<AddClassMembersResult>(`/classes/${groupId}/members`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => inval(qc),
  });
}

export function useUpdateClassMemberSlots(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enrollmentId, ...data }: UpdateClassMemberSlotsInput & { enrollmentId: string }) =>
      fetchApi<ClassGroupDetail>(`/classes/${groupId}/members/${enrollmentId}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success(ct('memberSlotsUpdated')); },
    onError: (e) => toast.error(e.message || ct('memberSlotsError')),
  });
}

export function useRemoveClassMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enrollmentId: string) => fetchApi(`/classes/${groupId}/members/${enrollmentId}`, { method: 'DELETE' }),
    onSuccess: () => { inval(qc); toast.success(ct('memberRemoved')); },
    onError: (e) => toast.error(e.message || ct('memberRemoveError')),
  });
}
